// @ts-nocheck
/*
 * Port of views/dashboard/server-management/edit.js (origin/main-dev, 259 lines).
 * BYTE-IDENTICAL — GET + POST edit flow, validation duplicated with
 * onError setToastProps fallback and nested error handlers preserved
 * verbatim. Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_SERVER_MANAGEMENT_EDIT } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { postApiCall, getApiCall } from '@/service/httpCall';
import RenderServerManagementForm from '@/features/server-management/ServerManagementForm';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';

function getApiUrl(url, id) {
  return url.replace(':id', id);
}

export default function ServerManagementEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [dataCenterId, setDataCenterId] = useState('');
  const [serverName, setServerName] = useState('');
  const [serverType, setServerType] = useState('');
  const [serverIP, setServerIP] = useState('');
  const [OS, setOS] = useState('');
  const [CPUCores, setCPUCores] = useState('');
  const [RAM, setRAM] = useState('');
  const [hardDisk, setHardDisk] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [countryData, setCountryData] = useState({});
  const [stateData, setStateData] = useState({});
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
    if (value !== onlyValid) {
      setError('Only letters, numbers, and underscore (_) are allowed.');
    } else {
      setError('');
    }
    setServerName(onlyValid);
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    const editApiUrl = getApiUrl(API_SERVER_MANAGEMENT_EDIT, id);
    getApiCall(editApiUrl, {}).then(onGetSuccess).catch(onError);
  }, [id]);

  function onGetSuccess(resp) {
    setDataCenterId(resp.data_center_id);
    setServerName(resp.server_name);
    setServerType(resp.type);
    setServerIP(resp.server_ip);
    setOS(resp.os);
    setCPUCores(resp.cpu_cores);
    setRAM(resp.ram);
    setHardDisk(resp.hard_disk);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      setToastProps({ message: JSON.stringify(err), type: 'danger' });
    }
  }

  function validateForm() {
    let valid = true;
    if (!dataCenterId.trim()) {
      valid = false;
      toast.error('Data center required');
    } else if (!serverName.trim()) {
      valid = false;
      toast.error('Server Name required');
    } else if (!String(serverType).trim()) {
      valid = false;
      toast.error('Server Type required');
    } else if (!serverIP.trim()) {
      valid = false;
      toast.error('Server IP required');
    } else {
      const ipRegex =
        /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
      if (!ipRegex.test(serverIP.trim())) {
        valid = false;
        toast.error('Invalid IPv4 address');
      }
    }

    if (!OS.trim()) {
      valid = false;
      toast.error('OS required');
    } else if (!CPUCores.trim()) {
      valid = false;
      toast.error('CPU Cores required');
    } else if (!RAM.trim()) {
      valid = false;
      toast.error('RAM required');
    } else if (!hardDisk.trim()) {
      valid = false;
      toast.error('Hard Disk required');
    }

    return valid;
  }

  async function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const params = {
      data_center: dataCenterId,
      server_name: serverName,
      type: Number(serverType),
      server_ip: serverIP,
      os: OS,
      cpu_cores: CPUCores,
      ram: RAM,
      hard_disk: hardDisk,
    };
    const editApiUrl = getApiUrl(API_SERVER_MANAGEMENT_EDIT, id);
    postApiCall(editApiUrl, params)
      .then(onServerManagementUpdateSuccess)
      .catch(onServerManagementUpdateError);

    function onServerManagementUpdateSuccess() {
      toast.success('Gateway updated successfully');
      setTimeout(() => {
        navigate(`/${ROUTES.SERVER_MANAGEMENT.LIST}`);
      }, 1500);
    }

    function onServerManagementUpdateError(err) {
      if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        setButtonDisable(false);
        toast.error(friendlyApiError(err));
      }
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update Gateway"
        description="Adjust the SIP gateway / media server configuration."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.SERVER_MANAGEMENT.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      <Card className="p-4">
        <form onSubmit={async (e) => { submitForm(e); }}>
          <RenderServerManagementForm
                  isEditMode={true}
                  dataCenterId={dataCenterId}
                  setDataCenterId={setDataCenterId}
                  serverName={serverName}
                  setServerName={setServerName}
                  serverType={serverType}
                  setServerType={setServerType}
                  serverIP={serverIP}
                  setServerIP={setServerIP}
                  OS={OS}
                  setOS={setOS}
                  CPUCores={CPUCores}
                  setCPUCores={setCPUCores}
                  RAM={RAM}
                  setRAM={setRAM}
                  hardDisk={hardDisk}
                  setHardDisk={setHardDisk}
                  countryData={countryData}
                  setCountryData={setCountryData}
                  stateData={stateData}
                  setStateData={setStateData}
                  handleChange={handleChange}
                  error={error}
                />

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to={`/${ROUTES.SERVER_MANAGEMENT.LIST}`}>Cancel</Link>
            </Button>
            <Button type="submit" disabled={buttonDisable}>
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Updating…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
}
